import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const formatAmount = (value, currency) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const amount = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${amount} ${currency}` : amount;
};

const AccountTransactionAdd = ({ open, onClose, token, initialAccountId = "" }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/account-transactions", token);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "account-transaction-add"],
    "shop/accounts/?page_size=all",
    token
  );
  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currencies", "account-transaction-add"],
    "shop/currencies/?page_size=all",
    token
  );

  const accounts = Array.isArray(accountsData) ? accountsData : accountsData?.results || [];
  const currencies = useMemo(
    () =>
      getUniqueCurrencies(
        Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
      ),
    [currenciesData]
  );

  const [accountId, setAccountId] = useState("");
  const [transactionCurrencyId, setTransactionCurrencyId] = useState("");
  const [transactionType, setTransactionType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().slice(0, 10));

  const selectedAccount = useMemo(
    () => accounts.find((account) => String(account.id) === String(accountId)) || null,
    [accounts, accountId]
  );
  const currencyBalances = selectedAccount?.currency_balances || [];
  const selectedCurrencyBalance = useMemo(() => {
    const balanceRow = currencyBalances.find(
      (row) => String(row.currency) === String(transactionCurrencyId)
    );
    if (balanceRow) return Number(balanceRow.balance || 0);
    if (
      selectedAccount &&
      String(selectedAccount.currency || "") === String(transactionCurrencyId)
    ) {
      return Number(selectedAccount.opening_balance || 0);
    }
    return 0;
  }, [selectedAccount, currencyBalances, transactionCurrencyId]);
  const selectedCurrencyTitle = useMemo(
    () =>
      currencies.find((currency) => String(currency.id) === String(transactionCurrencyId))?.title ||
      "",
    [currencies, transactionCurrencyId]
  );
  const amountValue = Number(amount || 0);
  const projectedBalance = useMemo(() => {
    if (!selectedCurrencyBalance && selectedCurrencyBalance !== 0) return null;
    if (!amount || Number.isNaN(amountValue)) return null;
    if (transactionType === "debit") {
      return selectedCurrencyBalance - amountValue;
    }
    return selectedCurrencyBalance + amountValue;
  }, [selectedCurrencyBalance, amount, amountValue, transactionType]);
  const selectedAccountLabel = selectedAccount
    ? `${selectedAccount.name || "-"}`
    : "";

  useEffect(() => {
    if (!open) return;
    setAccountId(initialAccountId ? String(initialAccountId) : "");
  }, [open, initialAccountId]);

  useEffect(() => {
    if (!selectedAccount) {
      setTransactionCurrencyId("");
      return;
    }

    const currencyStillAvailable = transactionCurrencyId
      ? currencies.some((currency) => String(currency.id) === String(transactionCurrencyId))
      : false;
    if (currencyStillAvailable) return;

    const preferredCurrency =
      (selectedAccount.currency ? String(selectedAccount.currency) : "") ||
      (currencies[0]?.id ? String(currencies[0].id) : "");
    setTransactionCurrencyId(preferredCurrency);
  }, [selectedAccount, currencies, transactionCurrencyId]);

  useEffect(() => {
    if (resetForm) {
      setAccountId(initialAccountId ? String(initialAccountId) : "");
      setTransactionCurrencyId("");
      setTransactionType("credit");
      setAmount("");
      setDescription("");
      setTransactionDate(new Date().toISOString().slice(0, 10));
      onClose();
    }
  }, [resetForm, onClose, initialAccountId]);

  const handleSubmit = () => {
    handleAdd({
      account: accountId,
      transaction_currency: transactionCurrencyId,
      transaction_type: transactionType,
      amount,
      description: description.trim() || null,
      transaction_date: transactionDate,
    });
  };

  const canSave =
    Boolean(accountId) &&
    Boolean(transactionCurrencyId) &&
    Boolean(transactionType) &&
    Boolean(amount) &&
    amountValue > 0 &&
    Boolean(transactionDate);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("accountTransaction.addTitle")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {initialAccountId ? (
            <TextField
              margin="dense"
              label={t("accountTransaction.account")}
              fullWidth
              value={selectedAccountLabel}
              disabled
            />
          ) : (
            <TextField
              select
              margin="dense"
              label={t("accountTransaction.account")}
              fullWidth
              required
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              {accountLoading ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                accounts.map((account) => (
                  <MenuItem key={account.id} value={String(account.id)}>
                    {account.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          )}

          <TextField
            select
            margin="dense"
            label={t("accountTransaction.transactionCurrency", "Transaction currency")}
            fullWidth
            required
            disabled={!selectedAccount || currencyLoading}
            value={transactionCurrencyId}
            onChange={(e) => setTransactionCurrencyId(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : currencies.length === 0 ? (
              <MenuItem value="" disabled>
                {t("accountTransaction.noCurrency", "No currencies found")}
              </MenuItem>
            ) : (
              currencies.map((currency) => (
                <MenuItem key={currency.id} value={String(currency.id)}>
                  {currency.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("accountTransaction.type")}
            fullWidth
            required
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
          >
            <MenuItem value="credit">{`${t("accountTransaction.credit")} +`}</MenuItem>
            <MenuItem value="debit">{`${t("accountTransaction.debit")} -`}</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            label={t("accountTransaction.amount")}
            type="number"
            inputProps={{ step: "0.01", min: "0.01" }}
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            helperText={
              selectedCurrencyTitle ? (
                <Box component="span" sx={{ display: "block" }}>
                  <Box component="span" sx={{ display: "block" }}>
                    {`${selectedCurrencyTitle} | ${t("accountTransaction.currentBalance", "Current Balance")}: ${formatAmount(
                      selectedCurrencyBalance,
                      selectedCurrencyTitle
                    )}`}
                  </Box>
                  {projectedBalance !== null && (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: transactionType === "debit" ? "error.main" : "success.main",
                        fontWeight: 600,
                      }}
                    >
                      {`${t("accountTransaction.afterTransaction", "After transaction")}: ${formatAmount(
                        projectedBalance,
                        selectedCurrencyTitle
                      )}`}
                    </Box>
                  )}
                </Box>
              ) : ""
            }
          />

          <TextField
            margin="dense"
            label={t("accountTransaction.transactionDate")}
            type="date"
            fullWidth
            required
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            margin="dense"
            label={t("accountTransaction.description")}
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("accountTransaction.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !canSave}
          sx={{ minWidth: 100 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t("accountTransaction.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountTransactionAdd;
